import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { searchCustomers } from "../api/customers";
import { getApiError } from "../api/errors";
import { getPricingPolicy, previewPricing } from "../api/pricing";
import { searchNativePosProducts } from "../api/nativePos";
import {
  approveQuotation,
  cancelQuotation,
  createQuotation,
  downloadQuotationPdf,
  getQuotation,
  getQuotations,
  rejectQuotation,
  sendQuotationWhatsApp,
  submitQuotation,
  updateQuotation,
} from "../api/quotations";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import VariantSnapshotLabel from "../components/VariantSnapshotLabel";
import { useAuth } from "../context/AuthContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import PageHeader from "../components/PageHeader";
import MainLayout from "../layouts/MainLayout";
import {
  createIdempotencyKey,
  formatDate,
  formatDateOnly,
  formatMoney,
  saveBlobResponse,
  titleCase,
} from "../utils/format";
import { concreteIdentityKey } from "../utils/productVariants";

const PAGE_SIZE = 24;
const STATUS_OPTIONS = [
  "",
  "draft",
  "pending_approval",
  "approved",
  "rejected",
  "cancelled",
  "expired",
];

function newLine() {
  return {
    key: createIdempotencyKey("line"),
    product: null,
    quantity: "1",
    override_type: "none",
    override_value: "",
  };
}

function statusColor(status) {
  if (status === "approved") return "success";
  if (status === "pending_approval") return "warning";
  if (["rejected", "cancelled", "expired"].includes(status)) return "error";
  return "default";
}

function normalizeLine(line) {
  const quantity = Number(line.quantity);
  if (!line.product?.public_id) throw new Error("Select a product or concrete variant for every line.");
  if (!Number.isInteger(quantity) || quantity < 1) {
    throw new Error("Every quotation quantity must be a whole number of at least 1.");
  }

  const payload = {
    product_public_id: line.product.public_id,
    product_variant_public_id: line.product.variant_public_id || null,
    quantity,
  };
  if (line.override_type !== "none") {
    const value = Number(line.override_value);
    if (!Number.isFinite(value) || value < 0) {
      throw new Error("Price overrides and discounts must be valid positive numbers.");
    }
    if (line.override_type === "discount") payload.discount_percent = value;
    if (line.override_type === "price") payload.requested_unit_price = value;
  }
  return payload;
}

function buildBuilderState(quotation, policy) {
  if (!quotation) {
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + Number(policy?.default_validity_days || 30));
    return {
      customer: null,
      lines: [newLine()],
      notes: "",
      terms: policy?.default_terms || "",
      valid_until: validUntil.toISOString().slice(0, 10),
      idempotency_key: createIdempotencyKey("quotation"),
    };
  }

  return {
    customer: quotation.customer || null,
    lines: quotation.items.map((item) => ({
      key: item.public_id,
      product: {
        public_id: item.product_public_id,
        variant_public_id: item.product_variant_public_id || null,
        variant_label: item.variant_label || "",
        name: item.product_name,
        brand: item.product_brand || "",
        external_id: item.external_id,
        price: item.list_price,
      },
      quantity: String(item.quantity),
      override_type: Number(item.unit_price) !== Number(item.list_price) ? "price" : "none",
      override_value:
        Number(item.unit_price) !== Number(item.list_price) ? String(item.unit_price) : "",
    })),
    notes: quotation.notes || "",
    terms: quotation.terms || "",
    valid_until: quotation.valid_until || "",
    idempotency_key: createIdempotencyKey("quotation-edit"),
  };
}

function QuotationBuilderDialog({
  open,
  quotation,
  policy,
  onClose,
  onSaved,
}) {
  const [state, setState] = useState(() => buildBuilderState(null, policy));
  const [customerInput, setCustomerInput] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [activeLineKey, setActiveLineKey] = useState(null);
  const [productInput, setProductInput] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const debouncedCustomerInput = useDebouncedValue(customerInput.trim(), 300);
  const debouncedProductInput = useDebouncedValue(productInput.trim(), 300);

  useEffect(() => {
    if (!open) return;
    setState(buildBuilderState(quotation, policy));
    setCustomerInput(quotation?.customer?.display_name || "");
    setCustomerOptions(quotation?.customer ? [quotation.customer] : []);
    setProductOptions([]);
    setProductInput("");
    setPreview(null);
    setError("");
  }, [open, policy, quotation]);

  useEffect(() => {
    let active = true;
    if (!open || debouncedCustomerInput.length < 1) {
      setCustomerOptions(state.customer ? [state.customer] : []);
      return undefined;
    }
    setCustomerLoading(true);
    searchCustomers(debouncedCustomerInput, 20)
      .then((response) => {
        if (active) setCustomerOptions(response.data?.items || []);
      })
      .catch(() => {
        if (active) setCustomerOptions(state.customer ? [state.customer] : []);
      })
      .finally(() => {
        if (active) setCustomerLoading(false);
      });
    return () => {
      active = false;
    };
  }, [debouncedCustomerInput, open, state.customer]);

  useEffect(() => {
    let active = true;
    if (!open || !activeLineKey || debouncedProductInput.length < 1) {
      setProductOptions([]);
      return undefined;
    }
    setProductLoading(true);
    searchNativePosProducts(debouncedProductInput, 30)
      .then((response) => {
        if (active) setProductOptions((response.data?.items || []).map((item) => ({
          ...item,
          public_id: item.product_public_id,
          variant_public_id: item.product_variant_public_id || null,
          name: item.product_name,
          external_id: item.sku || null,
        })));
      })
      .catch(() => {
        if (active) setProductOptions([]);
      })
      .finally(() => {
        if (active) setProductLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeLineKey, debouncedProductInput, open]);

  const updateLine = (key, patch) => {
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
    setPreview(null);
  };

  const requestItems = () => state.lines.map(normalizeLine);

  const runPreview = async () => {
    let items;
    try {
      items = requestItems();
    } catch (validationError) {
      setError(validationError.message);
      return null;
    }

    setPreviewing(true);
    setError("");
    try {
      const response = await previewPricing(items);
      setPreview(response.data);
      return response.data;
    } catch (requestError) {
      setError(getApiError(requestError, "Could not calculate the pricing preview."));
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  const save = async ({ submit = false } = {}) => {
    let items;
    try {
      items = requestItems();
      if (policy?.require_customer_for_quotation && !state.customer?.public_id) {
        throw new Error("The pricing policy requires a customer for every quotation.");
      }
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    const calculated = preview || (await runPreview());
    if (!calculated || !calculated.valid) {
      if (calculated && !calculated.valid) setError("The pricing preview contains blocking warnings.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        customer_public_id: state.customer?.public_id || null,
        items,
        notes: state.notes.trim() || null,
        terms: state.terms.trim() || null,
        valid_until: state.valid_until || null,
      };
      let saved;
      if (quotation) {
        const response = await updateQuotation(quotation.public_id, {
          ...payload,
          expected_version: quotation.version,
        });
        saved = response.data;
      } else {
        const response = await createQuotation({
          ...payload,
          idempotency_key: state.idempotency_key,
        });
        saved = response.data;
      }

      if (submit) {
        try {
          const submitted = await submitQuotation(saved.public_id);
          saved = submitted.data;
        } catch (submitError) {
          onSaved(
            saved,
            getApiError(
              submitError,
              "The draft was saved, but submission failed. Open it and submit again.",
            ),
            "warning",
          );
          return;
        }
      }

      onSaved(
        saved,
        submit ? "Quotation submitted for approval." : "Quotation draft saved.",
        "success",
      );
    } catch (requestError) {
      setError(getApiError(requestError, "Could not save the quotation."));
    } finally {
      setSaving(false);
    }
  };

  const removeLine = (key) => {
    setState((current) => ({
      ...current,
      lines: current.lines.length === 1
        ? current.lines
        : current.lines.filter((line) => line.key !== key),
    }));
    setPreview(null);
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="lg">
      <DialogTitle fontWeight={900}>
        {quotation ? `Edit ${quotation.quotation_number}` : "Create quotation draft"}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={customerOptions}
              value={state.customer}
              loading={customerLoading}
              inputValue={customerInput}
              onInputChange={(_, value) => setCustomerInput(value)}
              onChange={(_, value) => setState((current) => ({ ...current, customer: value }))}
              getOptionLabel={(option) => option?.display_name || ""}
              isOptionEqualToValue={(option, value) => option.public_id === value.public_id}
              renderOption={(props, option) => (
                <li {...props} key={option.public_id}>
                  <Box>
                    <Typography fontWeight={800}>{option.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.phone || option.email || option.external_id || "No contact details"}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={policy?.require_customer_for_quotation ? "Customer (required)" : "Customer (optional)"}
                  placeholder="Search name, phone, email, or external ID"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Valid until"
              InputLabelProps={{ shrink: true }}
              value={state.valid_until}
              inputProps={{ min: new Date().toISOString().slice(0, 10) }}
              onChange={(event) => setState((current) => ({ ...current, valid_until: event.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Currency" value={policy?.currency || "USD"} disabled />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Quotation items</Typography>
            <Typography variant="body2" color="text.secondary">
              Product prices are validated against stock, minimum prices, and discount policy.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => setState((current) => ({ ...current, lines: [...current.lines, newLine()] }))}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Add line
          </Button>
        </Stack>

        <Stack spacing={1.75}>
          {state.lines.map((line, index) => (
            <Card key={line.key} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 2.25 }}>
                <Grid container spacing={1.75} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Autocomplete
                      options={productOptions}
                      value={line.product}
                      loading={productLoading && activeLineKey === line.key}
                      onOpen={() => {
                        setActiveLineKey(line.key);
                        setProductInput(line.product?.name || "");
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason !== "reset") {
                          setActiveLineKey(line.key);
                          setProductInput(value);
                        }
                      }}
                      onChange={(_, value) => updateLine(line.key, { product: value })}
                      getOptionLabel={(option) => option ? `${option.name}${option.variant_label ? ` · ${option.variant_label}` : ""}` : ""}
                      isOptionEqualToValue={(option, value) => concreteIdentityKey(option.public_id, option.variant_public_id) === concreteIdentityKey(value.public_id, value.variant_public_id)}
                      renderOption={(props, option) => (
                        <li {...props} key={concreteIdentityKey(option.public_id, option.variant_public_id)}>
                          <Box>
                            <Typography fontWeight={800}>{option.name}</Typography>
                            {option.variant_label && <Typography variant="body2" color="primary.main" fontWeight={750}>{option.variant_label}</Typography>}
                            <Typography variant="caption" color="text.secondary">
                              {[option.brand, option.external_id, formatMoney(option.price, policy?.currency)]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label={`Product ${index + 1}`} placeholder="Search catalog" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Qty"
                      inputProps={{ min: 1, step: 1 }}
                      value={line.quantity}
                      onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2.25}>
                    <FormControl fullWidth>
                      <InputLabel>Override</InputLabel>
                      <Select
                        label="Override"
                        value={line.override_type}
                        onChange={(event) => updateLine(line.key, {
                          override_type: event.target.value,
                          override_value: "",
                        })}
                      >
                        <MenuItem value="none">List price</MenuItem>
                        <MenuItem value="discount">Discount %</MenuItem>
                        <MenuItem value="price">Unit price</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={9} sm={4} md={2.25}>
                    <TextField
                      fullWidth
                      type="number"
                      disabled={line.override_type === "none"}
                      label={line.override_type === "discount" ? "Discount %" : "Unit price"}
                      inputProps={{ min: 0, step: 0.01 }}
                      value={line.override_value}
                      onChange={(event) => updateLine(line.key, { override_value: event.target.value })}
                    />
                  </Grid>
                  <Grid item xs={3} sm={2} md={1}>
                    <Button
                      color="error"
                      disabled={state.lines.length === 1}
                      onClick={() => removeLine(line.key)}
                      sx={{ minWidth: 0, width: "100%" }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>

        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Internal/customer notes"
              value={state.notes}
              onChange={(event) => setState((current) => ({ ...current, notes: event.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Terms"
              value={state.terms}
              onChange={(event) => setState((current) => ({ ...current, terms: event.target.value }))}
            />
          </Grid>
        </Grid>

        {preview && (
          <Card sx={{ mt: 3, borderRadius: 4, bgcolor: "#0f172a", color: "white" }}>
            <CardContent sx={{ p: 2.75 }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                <Box>
                  <Typography variant="h6" fontWeight={900}>Pricing preview</Typography>
                  <Typography sx={{ color: "#cbd5e1", mt: 0.4 }}>
                    {preview.lines.length} line{preview.lines.length === 1 ? "" : "s"} · {preview.valid ? "Valid" : "Blocked"} · Policy v{preview.pricing_policy_version}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                    Role limit: {preview.lines[0]?.role_discount_limit_percent ?? 0}% · Engine: {preview.calculation_version}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Subtotal ${formatMoney(preview.subtotal, preview.currency)}`} sx={{ bgcolor: "#e2e8f0", fontWeight: 800 }} />
                  <Chip label={`Discount ${formatMoney(preview.discount_total, preview.currency)}`} sx={{ bgcolor: "#fef3c7", fontWeight: 800 }} />
                  <Chip label={`Tax ${formatMoney(preview.tax_total, preview.currency)}`} sx={{ bgcolor: "#dbeafe", fontWeight: 800 }} />
                  <Chip label={`Total ${formatMoney(preview.total, preview.currency)}`} color="success" sx={{ fontWeight: 900 }} />
                </Stack>
              </Stack>
              <Stack spacing={1.1} sx={{ mt: 2.25 }}>
                {preview.lines.map((line) => (
                  <Box key={concreteIdentityKey(line.product_public_id, line.product_variant_public_id)} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(255,255,255,.08)" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                      <Box>
                        <Typography fontWeight={900}>{line.product_name}</Typography>
                        {line.variant_label && <Typography variant="body2" sx={{ color: "#93c5fd" }} fontWeight={750}>{line.variant_label}</Typography>}
                        <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                          {line.quantity} × {formatMoney(line.unit_price, preview.currency)} · Discount {line.discount_percent}%
                        </Typography>
                      </Box>
                      <Typography fontWeight={900}>{formatMoney(line.line_total, preview.currency)}</Typography>
                    </Stack>
                    {line.gross_margin_percent !== null && line.gross_margin_percent !== undefined && (
                      <Typography variant="caption" sx={{ color: "#a7f3d0", display: "block", mt: 0.6 }}>
                        Gross margin {line.gross_margin_percent}% ({formatMoney(line.gross_margin_amount, preview.currency)}/unit)
                      </Typography>
                    )}
                    {line.effective_minimum_unit_price !== null && line.effective_minimum_unit_price !== undefined && (
                      <Typography variant="caption" sx={{ color: "#cbd5e1", display: "block" }}>
                        Effective minimum unit price: {formatMoney(line.effective_minimum_unit_price, preview.currency)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
              {preview.warnings?.length > 0 && (
                <Alert severity={preview.valid ? "warning" : "error"} sx={{ mt: 2.25, borderRadius: 3 }}>
                  {preview.warnings.join(" ")}
                </Alert>
              )}
              {preview.requires_management_approval && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
                  This pricing requires management approval before a PDF can be generated.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexWrap: "wrap" }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="outlined" onClick={runPreview} disabled={previewing || saving}>
          {previewing ? "Calculating…" : "Preview pricing"}
        </Button>
        <Button variant="outlined" onClick={() => save({ submit: false })} disabled={saving}>
          {saving ? "Saving…" : "Save draft"}
        </Button>
        <Button variant="contained" onClick={() => save({ submit: true })} disabled={saving}>
          {saving ? "Saving…" : "Save and submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function QuotationDetailDialog({
  open,
  quotation,
  loading,
  canEdit,
  canManage,
  onClose,
  onEdit,
  onSubmit,
  onDecision,
  onDownload,
  onRefresh,
  onSendWhatsapp,
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle fontWeight={900}>
        {quotation ? quotation.quotation_number : "Quotation"}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingPanel label="Loading quotation…" minHeight={320} />
        ) : quotation ? (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" fontWeight={900}>{quotation.quotation_number}</Typography>
                  <Chip label={titleCase(quotation.status)} color={statusColor(quotation.status)} sx={{ fontWeight: 800 }} />
                  <Chip label={`Version ${quotation.version}`} variant="outlined" />
                </Stack>
                <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                  {quotation.customer?.display_name || "No customer"} · Valid until {formatDateOnly(quotation.valid_until)}
                </Typography>
              </Box>
              <Box textAlign={{ xs: "left", md: "right" }}>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography variant="h4" fontWeight={900}>
                  {formatMoney(quotation.total, quotation.currency)}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              {[
                ["Subtotal", quotation.subtotal],
                ["Discount", quotation.discount_total],
                ["Tax", quotation.tax_total],
                ["Total", quotation.total],
              ].map(([label, value]) => (
                <Grid item xs={6} md={3} key={label}>
                  <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="h6" fontWeight={900}>{formatMoney(value, quotation.currency)}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Pricing policy v${quotation.pricing_policy_version || 1}`} size="small" />
              <Chip label={quotation.pricing_calculation_version || "pricing-v1"} size="small" variant="outlined" />
              {quotation.requires_management_approval && (
                <Chip label="Management approval required" color="warning" size="small" />
              )}
            </Stack>

            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 1.25 }}>Items</Typography>
              <Stack spacing={1.25}>
                {quotation.items.map((item) => (
                  <Box key={item.public_id} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                      <Box>
                        <VariantSnapshotLabel productName={item.product_name} variantLabel={item.variant_label} />
                        <Typography variant="body2" color="text.secondary">
                          {[item.product_brand, item.external_id].filter(Boolean).join(" · ") || "Catalog product"}
                        </Typography>
                      </Box>
                      <Typography fontWeight={900}>{formatMoney(item.line_total, quotation.currency)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">Qty: <strong>{item.quantity}</strong></Typography>
                      <Typography variant="body2">Unit: <strong>{formatMoney(item.unit_price, quotation.currency)}</strong></Typography>
                      <Typography variant="body2">Discount: <strong>{item.discount_percent}%</strong></Typography>
                      <Typography variant="body2">Tax: <strong>{item.tax_percent}%</strong></Typography>
                      {item.inventory_quantity_at_creation !== null && (
                        <Typography variant="body2">Stock snapshot: <strong>{item.inventory_quantity_at_creation}</strong></Typography>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>

            {(quotation.notes || quotation.terms) && (
              <Grid container spacing={2}>
                {quotation.notes && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.25, bgcolor: "#f8fafc", borderRadius: 3, height: "100%" }}>
                      <Typography fontWeight={900}>Notes</Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>{quotation.notes}</Typography>
                    </Box>
                  </Grid>
                )}
                {quotation.terms && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.25, bgcolor: "#f8fafc", borderRadius: 3, height: "100%" }}>
                      <Typography fontWeight={900}>Terms</Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>{quotation.terms}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}

            <Box>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} sx={{ mb: 1.25 }}>
                <Box>
                  <Typography variant="h6" fontWeight={900}>WhatsApp delivery</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The approved PDF is sent through the connected store WhatsApp account.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={onRefresh}>Refresh status</Button>
              </Stack>
              {!quotation.customer?.phone && (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Add a valid phone number to the selected customer before sending.
                </Alert>
              )}
              {quotation.deliveries?.length ? (
                <Stack spacing={1.2}>
                  {[...quotation.deliveries].reverse().slice(0, 5).map((delivery) => (
                    <Box key={delivery.public_id} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                        <Box>
                          <Typography fontWeight={850}>{delivery.recipient_phone}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Queued {formatDate(delivery.queued_at)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={titleCase(delivery.status)}
                          color={delivery.status === "failed" ? "error" : ["delivered", "read"].includes(delivery.status) ? "success" : delivery.status === "sent" ? "info" : "default"}
                          sx={{ fontWeight: 800 }}
                        />
                      </Stack>
                      {delivery.last_error && (
                        <Alert severity="error" sx={{ mt: 1.25, borderRadius: 2 }}>
                          {delivery.last_error}
                        </Alert>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This quotation has not been sent through WhatsApp yet.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ mb: 1.25 }}>History</Typography>
              <Stack spacing={1.2}>
                {quotation.events.map((event) => (
                  <Box key={event.public_id} sx={{ pl: 2, borderLeft: "3px solid #cbd5e1" }}>
                    <Typography fontWeight={850}>{titleCase(event.event_type)}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(event.created_at)}</Typography>
                    {event.reason && <Typography variant="body2" sx={{ mt: 0.4 }}>{event.reason}</Typography>}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </DialogContent>
      {quotation && (
        <DialogActions sx={{ px: 3, pb: 3, flexWrap: "wrap" }}>
          <Button onClick={onClose}>Close</Button>
          {quotation.status === "draft" && canEdit && (
            <>
              <Button variant="outlined" onClick={onEdit}>Edit draft</Button>
              <Button variant="contained" onClick={onSubmit}>Submit for approval</Button>
              <Button color="error" onClick={() => onDecision("cancel")}>Cancel draft</Button>
            </>
          )}
          {quotation.status === "pending_approval" && canEdit && (
            <Button color="error" onClick={() => onDecision("cancel")}>Cancel quotation</Button>
          )}
          {quotation.status === "pending_approval" && canManage && (
            <>
              <Button color="error" variant="outlined" onClick={() => onDecision("reject")}>Reject</Button>
              <Button color="success" variant="contained" onClick={() => onDecision("approve")}>Approve & generate PDF</Button>
            </>
          )}
          {quotation.pdf_available && (
            <Button variant="outlined" onClick={onDownload}>Download PDF</Button>
          )}
          {quotation.status === "approved" && quotation.pdf_available && canEdit && (
            <Button
              variant="contained"
              color="success"
              onClick={onSendWhatsapp}
              disabled={!quotation.customer?.phone}
            >
              Send PDF on WhatsApp
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default function Quotations() {
  const { canEdit, canManage, selectedWorkspace, selectedStore } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderQuotation, setBuilderQuotation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [decision, setDecision] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [acting, setActing] = useState(false);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState("");
  const [whatsAppIdempotencyKey, setWhatsAppIdempotencyKey] = useState("");
  const debouncedQuery = useDebouncedValue(query.trim(), 350);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError("");

      if (!selectedWorkspace?.public_id || !selectedStore?.public_id) {
        setQuotations([]);
        setTotal(0);
        setPendingTotal(0);
        setPolicy(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      try {
        const [listResponse, pendingResponse, policyResponse] = await Promise.all([
          getQuotations({
            query: debouncedQuery || undefined,
            status: statusFilter || undefined,
            page,
            page_size: PAGE_SIZE,
            _refresh: force ? Date.now() : undefined,
          }),
          getQuotations({ status: "pending_approval", page: 1, page_size: 1 }),
          getPricingPolicy(),
        ]);
        setQuotations(listResponse.data?.items || []);
        setTotal(Number(listResponse.data?.total || 0));
        setPendingTotal(Number(pendingResponse.data?.total || 0));
        setPolicy(policyResponse.data);
      } catch (requestError) {
        setError(getApiError(requestError, "Could not load quotations."));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [debouncedQuery, page, selectedStore?.public_id, selectedWorkspace?.public_id, statusFilter],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter, selectedStore?.public_id, selectedWorkspace?.public_id]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const approvedOnPage = useMemo(
    () => quotations.filter((item) => item.status === "approved").length,
    [quotations],
  );

  const openDetail = async (publicId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedQuotation(null);
    setError("");
    try {
      const response = await getQuotation(publicId);
      setSelectedQuotation(response.data);
    } catch (requestError) {
      setDetailOpen(false);
      setError(getApiError(requestError, "Could not load the quotation."));
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshSelected = async (publicId) => {
    const response = await getQuotation(publicId);
    setSelectedQuotation(response.data);
    return response.data;
  };

  const openCreate = () => {
    setBuilderQuotation(null);
    setBuilderOpen(true);
  };

  const openEdit = () => {
    setBuilderQuotation(selectedQuotation);
    setDetailOpen(false);
    setBuilderOpen(true);
  };

  const handleSaved = async (saved, message, severity = "success") => {
    setBuilderOpen(false);
    setBuilderQuotation(null);
    if (severity === "warning") setError(message);
    else setSuccess(message);
    await load({ force: true });
    await openDetail(saved.public_id);
  };

  const handleSubmit = async () => {
    if (!selectedQuotation) return;
    setActing(true);
    setError("");
    try {
      await submitQuotation(selectedQuotation.public_id);
      await refreshSelected(selectedQuotation.public_id);
      setSuccess("Quotation submitted for approval.");
      await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Could not submit the quotation."));
    } finally {
      setActing(false);
    }
  };

  const openDecision = (action) => {
    setDecision(action);
    setDecisionReason("");
  };

  const executeDecision = async () => {
    if (!selectedQuotation || !decision) return;
    if (decisionReason.trim().length < 3) {
      setError("A reason of at least 3 characters is required.");
      return;
    }
    setActing(true);
    setError("");
    try {
      if (decision === "approve") {
        await approveQuotation(selectedQuotation.public_id, decisionReason.trim());
      } else if (decision === "reject") {
        await rejectQuotation(selectedQuotation.public_id, decisionReason.trim());
      } else {
        await cancelQuotation(selectedQuotation.public_id, decisionReason.trim());
      }
      await refreshSelected(selectedQuotation.public_id);
      setSuccess(`Quotation ${decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "cancelled"}.`);
      setDecision(null);
      await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, `Could not ${decision} the quotation.`));
    } finally {
      setActing(false);
    }
  };

  const openWhatsAppDialog = () => {
    if (!selectedQuotation?.customer?.phone) {
      setError("Add a valid phone number to the customer before sending.");
      return;
    }
    setWhatsAppMessage("");
    setWhatsAppIdempotencyKey(createIdempotencyKey("quotation-whatsapp"));
    setWhatsAppDialogOpen(true);
  };

  const sendWhatsApp = async () => {
    if (!selectedQuotation || !whatsAppIdempotencyKey) return;
    setActing(true);
    setError("");
    try {
      await sendQuotationWhatsApp(selectedQuotation.public_id, {
        idempotency_key: whatsAppIdempotencyKey,
        message: whatsAppMessage.trim() || null,
      });
      await refreshSelected(selectedQuotation.public_id);
      setWhatsAppDialogOpen(false);
      setSuccess("Quotation PDF queued for WhatsApp delivery.");
    } catch (requestError) {
      setError(getApiError(requestError, "Could not send the quotation through WhatsApp."));
    } finally {
      setActing(false);
    }
  };

  const refreshDeliveryStatus = async () => {
    if (!selectedQuotation) return;
    setActing(true);
    setError("");
    try {
      await refreshSelected(selectedQuotation.public_id);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not refresh delivery status."));
    } finally {
      setActing(false);
    }
  };

  const downloadPdf = async () => {
    if (!selectedQuotation) return;
    setActing(true);
    setError("");
    try {
      const response = await downloadQuotationPdf(selectedQuotation.public_id);
      saveBlobResponse(response, `${selectedQuotation.quotation_number}.pdf`);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not download the quotation PDF."));
    } finally {
      setActing(false);
    }
  };

  return (
    <MainLayout>
      <PageHeader
        title="Quotations"
        description="Draft, approve, generate, and send tenant-safe quotation PDFs through WhatsApp."
        actions={(
          <>
            <Chip label={`${total.toLocaleString()} results`} sx={{ fontWeight: 800 }} />
            <Chip
              label={`${pendingTotal.toLocaleString()} awaiting approval`}
              color={pendingTotal ? "warning" : "default"}
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`${approvedOnPage} approved on page`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Button
              variant="outlined"
              onClick={() => load({ force: true })}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            {canEdit && (
              <Button variant="contained" onClick={openCreate}>
                Create quotation
              </Button>
            )}
          </>
        )}
      />

      {!canEdit && <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>You have read-only quotation access.</Alert>}
      {canEdit && !canManage && <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>You can create and submit drafts. Owners and admins control approvals.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Search quotations"
          placeholder="Quotation number or customer"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ maxWidth: 720, bgcolor: "white" }}
        />
        <FormControl sx={{ minWidth: 220, bgcolor: "white" }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUS_OPTIONS.map((value) => (
              <MenuItem key={value || "all"} value={value}>{value ? titleCase(value) : "All statuses"}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {canManage && pendingTotal > 0 && (
          <Button variant="outlined" color="warning" onClick={() => setStatusFilter("pending_approval")} sx={{ fontWeight: 800 }}>
            Open approval queue
          </Button>
        )}
      </Stack>

      {loading ? (
        <LoadingPanel label="Loading quotations…" />
      ) : quotations.length === 0 ? (
        <EmptyPanel
          icon="🧾"
          title={debouncedQuery || statusFilter ? "No matching quotations" : "No quotations yet"}
          description={debouncedQuery || statusFilter ? "Change the search or status filter." : "Create a draft from customers and catalog products, then submit it for approval."}
          action={canEdit && !debouncedQuery && !statusFilter ? <Button variant="contained" onClick={openCreate}>Create first quotation</Button> : null}
        />
      ) : (
        <>
          <Grid container spacing={2.5}>
            {quotations.map((quotation) => (
              <Grid item xs={12} sm={6} lg={4} key={quotation.public_id}>
                <Card sx={{ height: "100%", borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 14px 35px rgba(15,23,42,.05)" }}>
                  <CardContent sx={{ p: 2.75 }}>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={900} noWrap>{quotation.quotation_number}</Typography>
                        <Typography color="text.secondary" noWrap>{quotation.customer_name || "No customer"}</Typography>
                      </Box>
                      <Chip label={titleCase(quotation.status)} color={statusColor(quotation.status)} size="small" sx={{ fontWeight: 800 }} />
                    </Stack>
                    <Box sx={{ mt: 2.25, p: 2, bgcolor: "#f8fafc", borderRadius: 3 }}>
                      <Typography variant="caption" color="text.secondary">Total</Typography>
                      <Typography variant="h4" fontWeight={900}>{formatMoney(quotation.total, quotation.currency)}</Typography>
                    </Box>
                    <Stack spacing={0.8} sx={{ mt: 2 }}>
                      <Typography variant="body2">Valid until: <strong>{formatDateOnly(quotation.valid_until)}</strong></Typography>
                      <Typography variant="body2">Version: <strong>{quotation.version}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">Updated {formatDate(quotation.updated_at)}</Typography>
                    </Stack>
                    <Button variant="outlined" fullWidth onClick={() => openDetail(quotation.public_id)} sx={{ mt: 2.25, textTransform: "none", fontWeight: 800 }}>
                      Open quotation
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {pageCount > 1 && (
            <Stack alignItems="center" spacing={1} sx={{ mt: 4 }}>
              <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
              <Typography variant="caption" color="text.secondary">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
              </Typography>
            </Stack>
          )}
        </>
      )}

      <QuotationBuilderDialog
        open={builderOpen}
        quotation={builderQuotation}
        policy={policy}
        onClose={() => {
          setBuilderOpen(false);
          setBuilderQuotation(null);
        }}
        onSaved={handleSaved}
      />

      <QuotationDetailDialog
        open={detailOpen}
        quotation={selectedQuotation}
        loading={detailLoading || acting}
        canEdit={canEdit}
        canManage={canManage}
        onClose={() => setDetailOpen(false)}
        onEdit={openEdit}
        onSubmit={handleSubmit}
        onDecision={openDecision}
        onDownload={downloadPdf}
        onRefresh={refreshDeliveryStatus}
        onSendWhatsapp={openWhatsAppDialog}
      />

      <Dialog
        open={whatsAppDialogOpen}
        onClose={() => !acting && setWhatsAppDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle fontWeight={900}>Send quotation through WhatsApp</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 3 }}>
            The approved PDF will be sent to {selectedQuotation?.customer?.display_name || "the customer"} at {selectedQuotation?.customer?.phone || "the saved number"}. Sending is queued safely and delivery status is tracked.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Optional custom message"
            placeholder="Leave empty to use the professional default quotation message."
            value={whatsAppMessage}
            onChange={(event) => setWhatsAppMessage(event.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${whatsAppMessage.length}/1000`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setWhatsAppDialogOpen(false)} disabled={acting}>Cancel</Button>
          <Button variant="contained" color="success" onClick={sendWhatsApp} disabled={acting}>
            {acting ? "Queueing…" : "Confirm and send PDF"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(decision)} onClose={() => !acting && setDecision(null)} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={900}>{titleCase(decision)} quotation</DialogTitle>
        <DialogContent>
          <Alert severity={decision === "approve" ? "info" : "warning"} sx={{ mb: 2.5, borderRadius: 3 }}>
            {decision === "approve"
              ? "Approval creates the final PDF and records your decision permanently."
              : "This action is audited. Enter a clear business reason."}
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Decision reason"
            value={decisionReason}
            onChange={(event) => setDecisionReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDecision(null)} disabled={acting}>Cancel</Button>
          <Button
            variant="contained"
            color={decision === "approve" ? "success" : "error"}
            onClick={executeDecision}
            disabled={acting}
          >
            {acting ? "Processing…" : `Confirm ${decision}`}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
